import store from '@/store'
import recentWorkflowsApi from './recentWorkflowsApi'
import { workflowApiHelpers } from './workflowApiHelpers'
import cardHelpers from '@/helpers/cardHelpers'
import Checks from '@/helpers/checks'
import { EventBus } from '@/event-bus/event-bus.js'
import randomUUID from '@/utils/uuid'
import { SchemaType } from '@/constants/schema'
import { DomainModelRole } from '@/constants/cardTypes'
import safeStructuredClone from '@/utils/clone'
import { sanitizeCardDescription } from '@/utils/sanitize'
import { buildSchemasUpdateNote } from '@/utils/workflowUpdateNote'

export default class WorkflowApi {
  static async getWorkflowFromWorkflows (workflowId) {
    if (workflowId === store.state.workflow.workflow?.id) {
      return store.state.workflow.workflow
    } else {
      const workflow = store.state.workflows.workflows.find((workflow) => workflow.id === workflowId)
      return await store.dispatch('workflow/getWorkflowDetails', { id: workflowId, projectId: workflow.projectId })
    }
  }

  static async createWorkflow (workflow, silent = false) {
    return await store.dispatch('workflows/createWorkflow', { workflow, silent })
  }

  static async updateWorkflowMutation (projectId, input) {
    await store.dispatch('workflow/updateWorkflow', { projectId, input })
  }

  static async updateWorkflowNameMutation (projectId, input) {
    await store.dispatch('workflow/updateWorkflowName', { projectId, input })
  }

  static async updateWorkflowNoHistoryMutation (projectId, input) {
    await store.dispatch('workflow/updateWorkflowNoHistory', { projectId, input })
  }

  static async updateReleaseName (workflowId, releaseValue, releaseName) {
    const workflow = await this.getWorkflowFromWorkflows(workflowId)
    const releases = workflow.releases ? JSON.parse(JSON.stringify(workflow.releases)) : []

    const existingReleaseIndex = releases.findIndex((r) => r.value === releaseValue)

    if (existingReleaseIndex >= 0) {
      if (releaseName) {
        releases[existingReleaseIndex].name = releaseName
      } else {
        releases.splice(existingReleaseIndex, 1)
      }
    } else {
      releases.push({ value: releaseValue, name: releaseName })
    }

    await this.updateWorkflowMutation(workflow.projectId, {
      id: workflow.id,
      releases,
      expectedVersion: workflow.version,
      updateNote: {
        action: 'updated release name',
        target: `Release ${releaseValue}: ${releaseName}`
      }
    })
  }

  // this function can handle multiple changes to the diagram,
  // it extracts changes done to the svgGraph and svgLanes
  // arguments and updates the database accordingly
  static async updateWorkflow ({
    svgGraph = null,
    svgLanes = null,
    svgGroups = null,
    workflowProjectId = null,
    schemas = null,
    workflow,
    updateNote
  }, skipRefreshDiagram = false) {
    const events = workflow?.domainEvents
    if (!skipRefreshDiagram) {
      workflowApiHelpers.refreshDiagram(svgGraph, svgLanes, svgGroups)

      if (svgGraph !== null) {
        const { errors, errorList } = Checks.checkDiagramConsistency(svgGraph, svgLanes, svgGroups, events)
        if (errors !== 0) {
          store.commit('snackbar/showMessage', { content: 'Error updating workflow, try again or see log', timeout: 6000, color: 'red', location: 'center center' }, { root: true })
          console.error('Failed with ' + errors + ' errors. ' + errorList.toString())
          return
        }
      }
    }

    store.commit('workflow/setLoadingUpdate', true)
    // let eventsInput, lanesInput, groupsInput, deleteIndex, deleteVersion, deleteId, deletedParents
    let eventsInput, lanesInput, groupsInput
    let deleteEvents = []
    if (svgGraph) {
      // check if event has been deleted
      // ({ deleteIndex, deleteVersion, deleteId, deletedParents } = workflowApiHelpers.findDeletedEvent(svgGraph, workflow))
      const { deletedEvents, deletedParents } = workflowApiHelpers.findDeletedEvents(svgGraph, workflow)
      deleteEvents = deletedEvents
      const svgGraphUpdatedNodes = workflowApiHelpers.findUpdatedNodes(svgGraph, workflow, svgLanes)
      // add parents of deleted event to updated nodes (to ensure database consistency)
      if (deletedParents) {
        for (const parent of deletedParents) {
          if (!svgGraphUpdatedNodes[parent] && parent !== 'start' && !deletedEvents.map(event => event.deleteId).includes(parent)) {
            svgGraphUpdatedNodes[parent] = {
              id: svgGraph[parent].id,
              index: workflow.domainEvents.findIndex((event) => event.id === svgGraph[parent].id),
              version: svgGraph[parent].version
            }
          }
        }
      }
      eventsInput = Object.entries(svgGraphUpdatedNodes)
      eventsInput = eventsInput.map(([, node]) => {
        // we use index -2 to indicate that the workflow is empty
        // and -1 to indicate that we add an event
        // and any other index to indicate tha we update an existing event
        let index = -2
        if (workflow.domainEvents?.length > 0) {
          index = workflow.domainEvents.findIndex((event) => event.id === node.id)
        }
        return {
          id: node.id,
          index,
          description: node.name,
          laneId: node.laneId,
          laneIndex: node.laneIndex,
          groupId: node.groupId !== undefined ? node.groupId : undefined,
          type: node.type,
          conditionLabel: node.conditionLabel,
          conditionLabels: node.conditionLabels ? node.conditionLabels.map((item) => ({ parentId: item.parentId, name: item.name })) : undefined,
          parents: node.parents,
          offset: node.offset,
          cards: node.cards?.map((card) => workflowApiHelpers.filterCardFields(card)),
          color: node.color || undefined,
          subprocess: node.subprocess?.id ? node.subprocess.id : (node.subprocess || (node.subprocess === null ? null : undefined)),
          expectedVersion: node.version || undefined
        }
      })
    }
    if (svgLanes) {
      lanesInput = this.findUpdatedLanes(svgLanes, workflow)
    }
    if (svgGroups) {
      groupsInput = this.findUpdatedGroups(svgGroups, workflow)
    }
    if (lanesInput || groupsInput || eventsInput?.length > 0 || deleteEvents.length || schemas) {
      await this.updateWorkflowMutation(
        workflow.projectId,
        {
          id: workflow.id,
          projectId: workflowProjectId || undefined,
          domainEvents: eventsInput || undefined,
          lanes: lanesInput || undefined,
          groups: groupsInput || undefined,
          expectedVersion: workflow.version,
          expectedLanesVersion: workflow.lanesVersion,
          // deleteIndex: deleteIndex >= 0 ? deleteIndex : undefined,
          // deleteVersion: deleteVersion || undefined,
          // deleteId: deleteId || undefined,
          deletedEvents: deleteEvents.length > 0 ? deleteEvents : undefined,
          ...this.buildSchemasInput(schemas, workflow),
          updateNote
        }
      )
    }
    store.commit('workflow/setLoadingUpdate', false)
  }

  static async updateWorkflowName (workflow) {
    const { id, name, projectId, version, updateNote } = workflow

    await this.updateWorkflowNameMutation(projectId, {
      id,
      name: name || undefined,
      projectId: projectId || undefined,
      expectedVersion: version,
      updateNote
    })
  }

  static async updateSchemas ({ schemas, workflow, updateNote } = {}) {
    if (!workflow) {
      throw new Error('workflowApi.updateSchemas: workflow is required')
    }

    const normalizedSchemas = schemas
      ? schemas.map((item) => ({ ...item, id: item.id || randomUUID() }))
      : undefined

    const computedUpdateNote = updateNote || buildSchemasUpdateNote(workflow.schemas || [], normalizedSchemas || [])

    const delta = workflowApiHelpers.findUpdatedSchemas(normalizedSchemas, workflow.schemas)

    if (delta) {
      await store.dispatch('workflow/updateWorkflow', {
        projectId: workflow.projectId,
        input: {
          id: workflow.id,
          expectedVersion: workflow.version,
          schemas: delta.schemaUpdates.length > 0 ? delta.schemaUpdates : undefined,
          deletedSchemas: delta.deletedSchemas.length > 0 ? delta.deletedSchemas : undefined,
          updateNote: computedUpdateNote
        }
      })
    } else {
      if (!normalizedSchemas) {
        return
      }
      await store.dispatch('workflow/updateWorkflow', {
        projectId: workflow.projectId,
        input: {
          id: workflow.id,
          expectedVersion: workflow.version,
          schemas: normalizedSchemas,
          updateNote: computedUpdateNote
        }
      })
    }
  }

  static buildSchemasInput (schemas, workflow) {
    if (!schemas) {
      return {}
    }

    const normalizedSchemas = schemas.map((item) => ({ ...item, id: item.id || randomUUID() }))
    const delta = workflowApiHelpers.findUpdatedSchemas(normalizedSchemas, workflow.schemas)

    if (delta) {
      return {
        schemas: delta.schemaUpdates.length > 0 ? delta.schemaUpdates : undefined,
        deletedSchemas: delta.deletedSchemas.length > 0 ? delta.deletedSchemas : undefined
      }
    }

    return { schemas: normalizedSchemas }
  }

  static async addSchemaAndLinkCard ({ schema, cardId, eventId, workflow, svgGraph }) {
    const schemaId = schema.id || randomUUID()
    const newSchema = { ...schema, id: schemaId }

    const updatedSchemas = [...(workflow.schemas || []), newSchema]

    let updatedSvgGraph = null
    if (cardId && eventId && svgGraph) {
      updatedSvgGraph = safeStructuredClone(svgGraph)
      const cardIndex = updatedSvgGraph[eventId]?.cards?.findIndex((c) => c.id === cardId)
      if (cardIndex >= 0) {
        updatedSvgGraph[eventId].cards[cardIndex].schemaId = schemaId
      }
    }

    return await this.updateWorkflow({
      svgGraph: updatedSvgGraph,
      schemas: updatedSchemas,
      workflow,
      updateNote: {
        action: 'added schema',
        target: schema.name || ''
      }
    }, true)
  }

  static async undo (workflow) {
    // if sameAsVersion is set, then we are in a restored version
    const restoreVersion = workflow.sameAsVersion ? (workflow.sameAsVersion - 1) : workflow.version - 1
    await WorkflowApi.restorePreviousWorkflowVersion(restoreVersion, workflow)
  }

  static async redo (workflow) {
    if (workflow.sameAsVersion > 0 && workflow.sameAsVersion < (workflow.version - 1)) {
      const restoreVersion = (workflow.sameAsVersion + 1)
      await WorkflowApi.restorePreviousWorkflowVersion(restoreVersion, workflow)
    }
  }

  static async restorePreviousWorkflowVersion (restoreVersion, workflow) {
    if (restoreVersion >= 1) {
      await store.dispatch('workflow/restorePreviousWorkflowVersion', { restoreVersion, workflow })
    }
  }

  static async moveCard (workflowId, oldEventId, newEventId, cardId, release = undefined, sortkey = undefined) {
    const workflow = await this.getWorkflowFromWorkflows(workflowId)
    const { cardIndex, eventIndex } = workflowApiHelpers.getIndexesAndEventFromCardId(workflow, cardId)
    const input = {
      workflowId: workflow.id,
      expectedVersion: workflow.version,
      oldEventId,
      oldEventIndex: eventIndex,
      newEventId,
      newEventIndex: workflow.domainEvents.findIndex((event) => event.id === newEventId),
      cardId,
      cardIndex,
      release,
      sortkey
    }
    return await store.dispatch('workflow/moveCard', { input })
  }

  static getUpdateNoteFromCards (cards) {
    if (cards.length === 1) {
      if (cards[0].updateNote) {
        return cards[0].updateNote
      } else {
        return { action: 'updated card', target: cardHelpers.getCardTarget(cards[0]) }
      }
    } else {
      return {
        action: 'updated ' + cards.length + ' cards',
        target: cards.map((card) => cardHelpers.getCardTarget(card)).join('\n\n')
      }
    }
  }

  static async createCard (workflowId, eventId, card = {}) {
    if (!workflowId || !eventId) {
      return
    }
    const workflow = await this.getWorkflowFromWorkflows(workflowId)
    const eventIndex = workflow.domainEvents.findIndex((event) => event.id === eventId)
    if (!card.cardTypeId) {
      // set default card type if not set
      card.cardTypeId = store.getters['cardTypes/defaultCardType']?.id
    }

    const cardTypes = store.state.cardTypes.cardTypes
    const cardType = cardTypes.find((ct) => ct.id === card.cardTypeId)
    const isTagType = cardType?.domainModelRole === DomainModelRole.TAG

    if (!isTagType && !card.description) {
      card.description = '<p>New card</p>'
    }

    const cardData = {
      done: card.done || false,
      cardTypeId: card.cardTypeId,
      releaseNo: card.releaseNo,
      cardIndex: -1,
      sortkey: card.sortkey || cardHelpers.newSortkeyLastInList(workflow),
      expectedVersion: card.version,
      eventId,
      eventIndex,
      boundedContext: card.boundedContext
    }

    if (isTagType) {
      cardData.tags = card.tags || []
    } else {
      cardData.description = card.description
    }

    const input = {
      workflowId: workflow.id,
      expectedVersion: workflow.version,
      updateNote: {
        action: 'added card',
        target: cardHelpers.getCardTarget(card)
      },
      cards: [cardData]
    }

    return await store.dispatch('workflow/updateCards', { input })
  }

  static async updateCardsMutation (workflowId, cardsInput) {
    const workflow = await this.getWorkflowFromWorkflows(workflowId)
    const updateNote = this.getUpdateNoteFromCards(cardsInput)
    const input = {
      workflowId: workflow.id,
      expectedVersion: workflow.version,
      updateNote,
      cards: []
    }
    for (let cardInput of cardsInput) {
      const {
        eventId,
        eventIndex,
        cardIndex,
        card
      } = workflowApiHelpers.getIndexesAndEventFromCardId(workflow, cardInput.cardId)

      cardInput = Object.assign({ ...card }, cardInput, { cardIndex })

      input.cards.push({
        description: cardInput.description,
        done: cardInput.done,
        cardId: cardInput.cardId,
        cardTypeId: cardInput.cardTypeId || card.cardTypeId,
        releaseNo: cardInput.releaseNo === undefined ? card.releaseNo : cardInput.releaseNo,
        cardIndex: cardInput.cardIndex,
        sortkey: cardInput.sortkey,
        expectedVersion: cardInput.version,
        eventId,
        eventIndex,
        boundedContext: cardInput.boundedContext,
        schemaId: cardInput.schemaId,
        tags: cardInput.tags
      })
    }
    return await store.dispatch('workflow/updateCards', { input })
  }

  static async deleteCard (workflowId, deleteCardInput) {
    const workflow = await this.getWorkflowFromWorkflows(workflowId)
    const {
      eventId,
      eventIndex,
      cardIndex,
      card
    } = workflowApiHelpers.getIndexesAndEventFromCardId(workflow, deleteCardInput.cardId)
    const input = {
      projectId: workflow.projectId,
      workflowId: workflow.id,
      eventId,
      eventIndex,
      cardId: deleteCardInput.cardId,
      cardIndex,
      expectedVersion: workflow.version,
      cardDescription: cardHelpers.getCardTarget(card)
    }
    return await store.dispatch('workflow/deleteCard', { input })
  }

  static findLaneIdForNewShape (shape, options, lanes, svgGraph) {
    if (!lanes?.[0]?.id) {
      // no lanes
      return null
    }
    if (options.laneName) {
      // lookup lane id by name
      const lane = lanes.find((lane) => lane.name === options.laneName)
      return lane ? lane.id : null
    }
    if (options.createNewLaneIfNoLaneId) {
      return null
    }
    if (svgGraph[shape.parents[0]]) {
      // return the laneId of the selected event
      return svgGraph[shape.parents[0]]?.laneId
    }
    // empty diagram, use the first lane
    return lanes[0].id
  }

  static sanitizeShapeForCopy (shape, options, svgGraph, workflow) {
    const {
      id,
      type = 'bpmn:Task',
      parents,
      description = (type === 'bpmn:Task'
        ? workflowApiHelpers.getNextDescription(workflow.domainEvents)
        : workflowApiHelpers.getNextDecisionDescription(workflow.domainEvents)),
      laneId = this.findLaneIdForNewShape(shape, options, workflow.lanes, svgGraph),
      groupId,
      subprocess = null,
      color,
      cards,
      conditionLabels
    } = shape

    return {
      id,
      type,
      parents,
      description,
      laneId,
      groupId,
      subprocess,
      color,
      cards,
      conditionLabels
    }
  }

  static async addEventToWorkflow (
    shape,
    options = {},
    svgLanes = store.getters['svgGraphData/svgLanesData'],
    svgGraph = store.getters['svgGraphData/svgGraphData'],
    svgGroups = store.getters['svgGraphData/svgGroupsData'],
    workflow = store.state.workflow.workflow
  ) {
    shape = this.sanitizeShapeForCopy(shape, options, svgGraph, workflow)
    // UI update
    const newEventId = workflowApiHelpers.addNewShapeToSvgGraphAndAlignAllNodes(
      shape,
      options,
      svgLanes,
      svgGraph,
      svgGroups
    )
    // action to be displayed in the update note
    let action
    if (shape.type === 'bpmn:ExclusiveGateway') {
      action = 'added decision'
    } else {
      if (shape.subprocess) {
        action = 'added subprocess'
      } else {
        action = 'added event'
      }
    }
    // backend update
    await this.updateWorkflow({
      svgGraph,
      svgLanes,
      svgGroups,
      schemas: options?.schemas || undefined,
      workflow,
      updateNote: {
        action,
        target: shape.description
      }
    })
    return newEventId
  }

  static async deleteEventFromWorkflow (eventId, svgGraphData, svgLanesData, svgGroupsData, workflow) {
    // UI update
    // mutate copies of the computed svgGraphData & svgLanesData
    const svgGraph = JSON.parse(JSON.stringify(svgGraphData))
    const svgLanes = JSON.parse(JSON.stringify(svgLanesData))
    const svgGroups = JSON.parse(JSON.stringify(svgGroupsData))
    const event = svgGraph[eventId]
    const name = event.name
    workflowApiHelpers.deleteEventFromDiagram(event.id, svgGraph, svgGroups)

    // action to be displayed in the update note
    let action
    switch (event.type) {
      case 'bpmn:Task':
        if (event.subprocess) {
          action = 'deleted subprocess'
        } else {
          action = 'deleted event'
        }
        break
      case 'bpmn:ExclusiveGateway':
        action = 'deleted decision'
        break
    }

    // backend update
    await WorkflowApi.updateWorkflow({
      svgGraph,
      svgGroups,
      svgLanes,
      workflow,
      updateNote: {
        action,
        target: name
      }
    })
  }

  static async deleteEventsFromWorkflow (eventIds, svgGraphData, svgLanesData, svgGroupsData, workflow) {
    // UI update
    // mutate copies of the computed svgGraphData & svgLanesData
    const svgGraph = JSON.parse(JSON.stringify(svgGraphData))
    const svgLanes = JSON.parse(JSON.stringify(svgLanesData))
    const svgGroups = JSON.parse(JSON.stringify(svgGroupsData))
    const name = eventIds.map(eventId => svgGraph[eventId].name).join(', ')
    let numEventsDeleted = 0
    let numDecisionsDeleted = 0
    for (const eventId of eventIds) {
      const event = svgGraph[eventId]
      if (event.type === 'bpmn:ExclusiveGateway') {
        numDecisionsDeleted++
      } else {
        numEventsDeleted++
      }
    }
    // action to be displayed in the update note
    const action = 'deleted ' +
      (numEventsDeleted ? numEventsDeleted + ' event(s)' : '') +
      (numEventsDeleted && numDecisionsDeleted ? ' and ' : '') +
      (numDecisionsDeleted ? numDecisionsDeleted + ' decision(s)' : '')

    workflowApiHelpers.deleteEventsFromDiagram(eventIds, svgGraph, svgGroups)

    // backend update
    await WorkflowApi.updateWorkflow({
      svgGraph,
      svgGroups,
      svgLanes,
      workflow,
      updateNote: {
        action,
        target: name
      }
    })
  }

  static async deleteConnectionFromEvent (parentId, childId, svgGraph, svgLanes, svgGroups, workflow) {
    // UI update
    workflowApiHelpers.deleteConnectionFromEvent(parentId, childId, svgGraph, svgGroups)
    // backend update
    await this.updateWorkflow({
      svgGraph,
      svgLanes,
      svgGroups,
      workflow,
      updateNote: {
        action: 'deleted connection to',
        target: name
      }
    })
  }

  static async addConnectionToEvent (sourceEventId, targetEventId, svgGraph, svgLanes, svgGroups, workflow) {
    // UI update
    workflowApiHelpers.addConnectionToEvent(sourceEventId, targetEventId, svgGraph, svgGroups)

    // backend update
    this.updateWorkflow({
      svgGraph,
      svgLanes,
      svgGroups,
      workflow,
      updateNote: {
        action: 'added connection to',
        target: svgGraph[targetEventId].name
      }
    })
  }

  static updateGroupStartingPosition (groupId, svgGraph, svgLanes, svgGroups, workflow) {
    // UI update
    workflowApiHelpers.updateGroupStartingPosition(svgGraph, svgGroups)

    // backend update
    this.updateWorkflow({
      svgGraph,
      svgLanes,
      svgGroups,
      workflow,
      updateNote: {
        action: 'moved group',
        target: svgGroups[groupId].name
      }
    })
  }

  static findUpdatedLanes (svgLanes, workflow) {
    const sortedNewLanes = Object.values(svgLanes).sort((a, b) => a.index - b.index)

    const oldLanes = workflow.lanes || []
    let lanesInput

    const hasChanges = sortedNewLanes.length !== oldLanes.length ||
      sortedNewLanes.some((lane, index) => {
        return lane.id !== oldLanes[index]?.id || lane.name !== oldLanes[index]?.name
      })

    if (hasChanges) {
      lanesInput = sortedNewLanes.map((lane) => ({
        id: lane.id,
        name: lane.name
      }))
    }

    return lanesInput
  }

  static findUpdatedGroups (svgGroup, workflow) {
    let groupsInput = Object.entries(svgGroup)
    let index = 0
    let dirtyFlag = false
    for (const group of Object.values(svgGroup)) {
      const oldGroup = workflow.groups?.[index]
      if (
        !oldGroup ||
        oldGroup.name !== group.name ||
        oldGroup.startSlotX !== group.startSlotX
      ) {
        dirtyFlag = true
      }
      index++
    }
    if (!dirtyFlag) {
      if (workflow.groups?.length !== groupsInput?.length) {
        dirtyFlag = true
      }
    }
    if (dirtyFlag) {
      groupsInput = groupsInput.map(([, group]) => ({
        id: group.id,
        name: group.name,
        startSlotX: group.startSlotX
      }))
    } else {
      groupsInput = undefined
    }
    return groupsInput
  }

  static deleteGroup (group, svgGraph, svgLanes, svgGroups, workflow) {
    // UI update
    workflowApiHelpers.deleteGroup(group.id, svgGraph, svgGroups)

    // backend update
    this.updateWorkflow({
      svgGraph,
      svgLanes,
      svgGroups,
      workflow,
      updateNote: {
        action: 'deleted group',
        target: group.name
      }
    })
  }

  static async deleteCardAndModel (event, card, svgGraph, workflow) {
    const updatedSvgGraph = safeStructuredClone(svgGraph)

    updatedSvgGraph[event.id].cards = updatedSvgGraph[event.id].cards?.reduce((acc, item) => {
      if (item.id !== card.id) {
        acc.push({
          id: item.id,
          description: item.description,
          releaseNo: item.releaseNo,
          sortkey: item.sortkey,
          cardTypeId: item.cardTypeId,
          done: item.done,
          boundedContext: item.boundedContext,
          creatorId: item.creatorId,
          version: item.version,
          created: item.created,
          schemaId: item.schemaId
        })
      }

      return acc
    }, [])

    const schema = (workflow.schemas ?? []).find((item) => item.id === card.schemaId)

    // Check if any other cards still reference this schema
    const isSchemaReferencedElsewhere = card.schemaId && Object.values(updatedSvgGraph).some((eventData) =>
      eventData.cards?.some((c) => c.schemaId === card.schemaId)
    )

    const preservedSchemaTypes = [SchemaType.ENTITY, SchemaType.QUERY, SchemaType.DOMAIN_EVENT]
    const updatedSchemas = card.schemaId &&
    schema &&
    !preservedSchemaTypes.includes(schema.type) &&
    !isSchemaReferencedElsewhere
      ? (workflow.schemas || []).filter((schema) => schema.id !== card.schemaId)
      : undefined

    const target = card.description
      ? sanitizeCardDescription(card.description)
      : cardHelpers.getCardTarget(card)

    return await this.updateWorkflow({
      svgGraph: updatedSvgGraph,
      schemas: updatedSchemas,
      workflow,
      updateNote: {
        action: 'deleted card',
        target
      }
    }, true)
  }

  static async importWorkflow (workflowId, projectId, workflowData) {
    return await store.dispatch('workflow/importWorkflow', { workflowId, projectId, workflowData })
  }
}

export async function deleteWorkflow (workflowId, projectId) {
  store.commit('loading/startLoading')
  await store.dispatch('workflows/deleteWorkflow', { workflowId, projectId })
  recentWorkflowsApi.delete(workflowId)
  EventBus.$emit('refresh-dashboard')
  store.commit('loading/stopLoading')
}

export async function cloneWorkflow (targetProjectId, workflowId) {
  return await store.dispatch('workflows/cloneWorkflow', { targetProjectId, workflowId })
}
